<template>
	<div
		class="cog-picker cog-select cog-input"
		:class="{'is-read-only': readonly, 'cog-picker--allow-other': internalAllowOther}"
	>
		<template v-if="readonly">
			<span :class="{'cog-image-item': selectedImage.value}"><template v-if="selectedImage.value"><img v-if="!isSvg(selectedImage.value) && selectedImage.type === 'image'" :src="selectedImage.value" alt="" height="50" width="50" class="cog-lookup-image cog-avatar" /><div v-else-if="selectedImage.type === 'choiceImage'" :style="{ backgroundImage: `url(${selectedImage.value})`}" height="50" width="50" class="cog-choice-image" /><c-generated-avatar v-else class="cog-select-image__prefix" :svg="selectedImage.value" /></template>{{ displayValue || "&nbsp;" }}</span>
		</template>
		<template v-else>
			<c-picker-native v-if="useNative" :placeholder="placeholder" :show-placeholder="!hidePlaceholder">
				<template v-if="internalAllowOther">
					<input
						:id="id ? 'cog-'+id : null"
						v-model="internalValue"
						type="text"
						:list="id ? `cog-${id}-list` : null"
						:placeholder="placeholder"
						class="el-input__inner"
						@keydown="handleNativeMenuOpen"
						@click="handleNativeMenuOpen"
						@change="handleNativeChange"
						@focus="handleFocus"
						@blur="handleBlur"
					/>
					<datalist :id="id ? `cog-${id}-list` : null">
						<option v-if="showEmptyOption" :value="null">&nbsp;</option>
						<option v-for="item in options" :key="item.value" :value="item.value">{{ item.displayValue }}</option>
					</datalist>
				</template>
				<select
					v-else
					:id="id ? 'cog-'+id : null"
					ref="nativeSelect"
					v-model="internalValue"
					v-bind="$attrs"
					:title="title"
					class="el-input__inner"
					:class="{'is-null': !value}"
					@keydown="handleNativeMenuOpen"
					@click="handleNativeMenuOpen"
					@change="handleNativeChange"
					@focus="handleFocus"
					@blur="handleBlur"
				>
					<option v-if="showEmptyOption" :value="null">&nbsp;</option>
					<option v-for="item in options" :key="item.value" :value="item.value">{{ item.displayValue }}</option>
				</select>
				<template v-if="mobile && showSelectIcon" #icon>
					<i-chevron />
				</template>
			</c-picker-native>
			<!--	For autocomplete drama, see first comment on #17543	-->
			<el-select
				v-else
				:id="id ? 'cog-'+id : null"
				ref="select"
				v-model="internalValue"
				v-bind="$attrs"
				:placeholder="placeholder"
				:allow-create="internalAllowOther"
				no-data-text=" "
				default-first-option
				:filterable="filterable"
				:filter-match="filterMatch"
				:alternative-filter="alternativeFilter"
				:editable="internalAllowOther"
				:automatic-dropdown="false"
				:popper-class="popperClasses"
				:autocomplete="autocomplete"
				:title="title"
				lazy-options
				@focus="handleFocus"
				@blur="handleBlur"
				@change="handleChange"
				@change.native="handleUserInputChange"
				@visible-change="handleSelectVisibleChange"
			>
				<el-option v-if="showEmptyOption" :value="null" class="cog-select-option--empty">&nbsp;</el-option>
				<el-option
					v-for="(item, index) in options"
					:key="item.value + index"
					:value="item.value"
					:label="item.displayValue || nbsp"
					:style="{ order: index}"
				>
					<div :class="{'cog-image-item cog-image-item__dropdown': item.svg || item.image || item.choiceImage}">
						<div v-if="item.image && !item.choiceImage" class="cog-flex">
							<img :src="item.image" alt="" height="50" width="50" class="cog-lookup-image cog-avatar" />
						</div>
						<div v-else-if="item.choiceImage" class="cog-flex">
							<div :style="{ backgroundImage: `url(${item.choiceImage})`}" height="50" width="50" class="cog-choice-image" />
						</div>
						<c-generated-avatar v-if="item.svg" :svg="item.svg"></c-generated-avatar>
						<span>{{ item.displayValue || item.value || nbsp }}</span>
					</div>
				</el-option>
				<template v-if="!prefixDefined && selectedImage.value && !dropdownVisible" #prefix>
					<img
						v-if="!isSvg(selectedImage.value) && selectedImage.type === 'image'"
						:src="selectedImage.value"
						alt=""
						height="50"
						width="50"
						class="cog-lookup-image cog-avatar cog-select-image__prefix"
					/>
					<div
						v-else-if="selectedImage.type === 'choiceImage'"
						:style="{ backgroundImage: `url(${selectedImage.value})`}"
						height="50"
						width="50"
						class="cog-choice-image cog-select-image__prefix"
					/>
					<c-generated-avatar
						v-else
						class="cog-select-image__prefix"
						:svg="selectedImage.value"
					/>
				</template>
				<template v-if="showSelectIcon" #suffix>
					<span @click="handleIconClick" @mousedown="handleIconMousedown">
						<slot name="suffix">
							<i-chevron />
						</slot>
					</span>
				</template>
			</el-select>
		</template>
	</div>
</template>

<script>
	import 'src/style/_popper.scss';
	import 'src/style/_motion.scss';
	import 'src/style/_input-icon.scss';
	import 'src/style/_scrollbar.scss';
	import 'src/style/_dropdown.scss';

	import Vue from 'vue';
	import { Option } from '@cognitoforms/element-ui';
	import CPickerNative from 'src/components/PickerNative.vue';
	import IChevron from './../assets/chevron.svg';
	import focus from '../mixins/focus.ts';
	import CSelectInput from './SelectInput.ts';
	import CGeneratedAvatar from './GeneratedAvatar.vue';
	import { ensureMinHeight } from './form-resizing';
	import { isSafari } from '@cognitoforms/utils/user-agent';

	Vue.use(Option);

	const MIN_REFOCUS_DELAY = 25;
	const SELECT_MENU_MAXHEIGHT = 200;

	export default {
		name: 'CSelect',
		components: { IChevron, CPickerNative, 'el-select': CSelectInput, CGeneratedAvatar },
		mixins: [focus],
		inject: {
			$resource: {},
			formEvents: {},
			flags: { default: () => ({}) }
		},
		inheritAttrs: false,
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			// eslint-disable-next-line vue/require-prop-types
			value: { default: '' },
			readonly: Boolean,
			allowOther: Boolean,
			chameleon: Boolean,
			mobile: Boolean,
			showEmptyOption: { type: Boolean, default: true },
			allowArrowsToChangeValue: { type: Boolean, default: true },
			placeholder: { type: String, default: null },
			options: { type: Array, default: null },
			scope: { type: String, default: '0' },
			additionalPopperClass: { type: String, default: '' },
			filterable: { type: Boolean, default: true },
			filterMatch: { type: String, default: 'contains' },
			id: { type: String, required: true },
			title: { type: String, default: null },
			autocomplete: { type: String, default: 'prevent' },
			alternativeFilter: { type: Function, default: null }
		},
		data() {
			return {
				internalValue: this.value,
				hidePlaceholder: this.allowOther || !!this.value,
				internalAllowOther: this.allowOther,
				dropdownVisible: false
			};
		},
		computed: {
			showSelectIcon() {
				// Non-number/text dropdown field (ie: time field) icons should always be displayed
				if (this.$slots.suffix)
					return true;

				return !this.internalAllowOther;
			},
			displayValue() {
				if (this.value && Array.isArray(this.options)) {
					const selected = this.options.find(o => o.value === this.value);
					return selected ? selected.displayValue : this.value;
				}
				return '';
			},
			useNative() {
				return !(isSafari(window.navigator) && this.allowOther) && (this.chameleon || this.mobile) && (!this.hasImageOption);
			},
			nbsp() {
				return '\u00A0';
			},
			popperClasses() {
				return [`cog-cognito cog-${this.scope}`,
					`cog-cognito--${(this.chameleon ? 'chameleon' : 'styled')}`,
					'cog-select-popper',
					`${this.options && this.options.length > 100 ? 'el-select-dropdown--long' : ''}`,
					`${this.flags.protectCss ? 'cog-cognito--protect-css' : ''}`,
					`${this.additionalPopperClass}`
				].join(' ');
			},
			selectedImage() {
				if (this.value) {
					const matchedOption = this.options?.find(
						option => option.value === this.internalValue
					);

					if (matchedOption) {
						if (matchedOption.choiceImage) {
							return { type: 'choiceImage', value: matchedOption.choiceImage };
						}
						else if (matchedOption.image) {
							return { type: 'image', value: matchedOption.image };
						}
						else if (matchedOption.svg) {
							return { type: 'svg', value: matchedOption.svg };
						}
					}
				}

				return { type: null, value: '' };
			},
			hasImageOption() {
				return this.options?.some(dropdownOption => dropdownOption.image || dropdownOption.svg || dropdownOption.choiceImage);
			},
			prefixDefined() {
				return !!this.$slots.prefix;
			}
		},
		watch: {
			value: {
				immediate: true,
				handler: function(valueProp) {
					// Selects can't contain a null value so blank will need to be converted
					this.internalValue = valueProp === '' ? null : valueProp;
					this.hidePlaceholder = this.internalAllowOther || !!this.value;
				}
			}
		},
		methods: {
			// Allows a user to type in a value and then click outside of the select to set a value
			handleUserInputChange(e) {
				if (!this.internalAllowOther) {
					const matchedOption = this.options.filter((o)=>
						o.value.toLowerCase() === e.target.value.toLowerCase() ||
						o.displayValue.toLowerCase() === e.target.value.toLowerCase()
					)[0];

					if (matchedOption)
						this.handleChange(matchedOption.value);
				}
			},
			handleBlur(e) {
				this.removeClassOnBlur();
				this.$emit('blur', e);
				this.hidePlaceholder = this.internalAllowOther || !!this.value;
			},
			handleFocus(e) {
				this.addClassOnFocus();
				this.$emit('focus', e);
			},
			handleChange(value) {
				this.internalValue = value;
				this.$emit('change', value);
			},
			handleNativeMenuOpen() {
				// Native select (on desktop) can overlap with faux placeholder text. See #18599
				this.hidePlaceholder = true;
			},
			handleNativeChange(ev) {
				// IOS needs the select to be refocused after selection if "done" is tapped. Cf. #20786
				// Any number less than 20 results in inconsistent success.
				setTimeout(() => {
					if (document.activeElement === document.body)
						this.$refs.nativeSelect.focus();
				}, MIN_REFOCUS_DELAY);
				const value = ev.target.value;
				this.internalValue = value;
				this.$emit('change', value, ev);
			},
			handleIconClick(e) {
				// toggle menu
				this.$refs.select.visible = !this.$refs.select.visible;
				e.stopPropagation();

				// keep focus on select regardless
				this.$refs.select.$el.querySelector('input').focus();
			},
			handleIconMousedown(e) {
				// prevent handleBlur from being called as a result of clicking the chevron
				e.preventDefault();
			},
			handleSelectVisibleChange(visible) {
				// Re-emit the "visible-change" event
				this.$emit('visible-change', visible);
				if (this.formEvents)
					if (!visible) {
						this.dropdownVisible = false;
						ensureMinHeight(this.formEvents, 0);
					}
					else {
						this.dropdownVisible = true;
						const formHeight = document.documentElement.clientHeight;
						const { top: selectTop, bottom: selectBottom } = this.$el.getBoundingClientRect();
						const selectBottomBounds = Math.ceil(selectBottom) + SELECT_MENU_MAXHEIGHT;
						// If there's no room on top or bottom of the form for the select menu
						if (Math.ceil(selectTop) < SELECT_MENU_MAXHEIGHT && selectBottomBounds > formHeight)
							// Make the form tall enough to accomodate the select menu on the bottom
							ensureMinHeight(this.formEvents, selectBottomBounds);
					}
			},
			isSvg(image) {
				return image && image.startsWith('<svg viewBox=');
			}
		}
	};
</script>

<style lang="scss">
$SELECT_MENU_MAXHEIGHT: 200px;

#{$specificity-base} {

	.cog-picker--allow-other {

		.el-input__inner {
			cursor: text;
		}
	}

	@include at-root {

		&.cog-iframe-embed .el-select-dropdown__wrap {
			max-height: $SELECT_MENU_MAXHEIGHT;
		}
	}

	.cog-cognito--chameleon select {
		width: 100%;
	}

	.cog-select-image__prefix {

		&.cog-generated-avatar,
		img {
			position: relative;
		}
	}

	.cog-image-item__dropdown {
		margin-left: -.3em;
	}
}
</style>
